import { useCallback, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';

//* HOC's
import { withUIContext } from '@/context';

//* Helpers
import { getParamsByKey } from '@/helpers/_functions';

//* Components
import FilterItem from './FilterItem';
import Text from '@/components/common/Text';
import MobileFilterItem from './MobileFilterItem';
import SelectedItem from './SelectedItem';

//* Style
import FiltersStyle from './style';

//! Price Items Array
const priceItemsArray = [
    { id: '0,10', name: 'Under 10$' },
    { id: '10,50', name: '10$ - 50$' },
    { id: '50,100', name: '50$ - 100$' },
    { id: '100,1000000', name: 'Above 100$' },
];

//! Sale Items Array
const saleItemsArray = [
    { id: 'discount', name: 'Discounts' },
    { id: 'best_seller', name: 'Best Sellers' },
];

const selectedFiltersTypes = [
    { filterType: 'brands', filterKey: 'brand', multiple: true },
    { filterType: 'tags', filterKey: 'tag', multiple: false },
    { filterType: 'sale_by', filterKey: 'sale_by', multiple: false },
    { filterType: 'price', filterKey: 'price', multiple: false },
]

const Filters = ({ filters, searchParams, onFilterChange, isMobile = false, winWidth }) => {
    //! Next Navigation
    const pathname = usePathname();
    const router = useRouter();

    const Comp = useMemo(() => (isMobile ? MobileFilterItem : FilterItem), [isMobile]);

    const objFilters = {
        ...filters,
        price: priceItemsArray,
        sale_by: saleItemsArray
    }

    //! Selected Filters
    const selectedFilters = useCallback(({ filterType, filterKey, multiple = false, objItems }) => {
        const selectedValues = getParamsByKey(searchParams, filterKey);
        return objFilters[filterType].filter((item) => (!multiple ? (filterType == 'price' ? item.id.split(',')[0] == selectedValues?.[0] : item.id == selectedValues?.[0]) : selectedValues?.includes(String(item.id))));
    }, [objFilters, searchParams]);

    //! Clear Filter
    const clearFilter = useCallback(() => {
        router.push(pathname);
    }, [pathname]);

    const makeCategories = useMemo(() => {
        return filters?.categories?.length > 0
            ? filters.categories.map((category, index) => (
                <Comp
                    key={index}
                    filterKey={'cat'}
                    filterType={'url'}
                    url={category.url}
                    title={category.name}
                    items={category.children}
                    searchParams={searchParams}
                    onFilterChange={onFilterChange}
                />
            ))
            : null;
    }, [Comp, filters, searchParams]);

    const makeSale = useMemo(() => {
        return (
            <Comp
                title={'Sale'}
                filterKey={'sale_by'}
                items={saleItemsArray}
                searchParams={searchParams}
                onFilterChange={onFilterChange}
            />
        );
    }, [Comp, filters, searchParams]);

    const makeBrands = useMemo(() => {
        return filters?.brands?.length > 0 ? (
            <Comp
                multiple
                title={'Brand'}
                filterKey={'brand'}
                items={filters.brands}
                searchParams={searchParams}
                onFilterChange={onFilterChange}
            />
        ) : null;
    }, [Comp, filters, searchParams]);

    const makePrice = useMemo(() => {
        return (
            <Comp
                title={'Price'}
                filterKey={'price'}
                items={priceItemsArray}
                searchParams={searchParams}
                onFilterChange={onFilterChange}
            />
        );
    }, [Comp, filters, searchParams]);

    const makeReligiousBased = useMemo(() => {
        return filters?.tags?.filter((item) => item.name == 'Kosher' || item.name == 'Halal').length > 0 ? (
            <Comp
                filterKey={'tag'}
                title={'Religious Based'}
                searchParams={searchParams}
                onFilterChange={onFilterChange}
                items={filters.tags.filter((item) => item.name == 'Kosher' || item.name == 'Halal')}
            />
        ) : null;
    }, [Comp, filters, searchParams]);

    const makeDietary = useMemo(() => {
        return filters?.tags?.filter((item) => item.name != 'Kosher' && item.name != 'Halal').length > 0 ? (
            <Comp
                isLast
                title={'Dietary'}
                filterKey={'tag'}
                searchParams={searchParams}
                onFilterChange={onFilterChange}
                items={filters.tags.filter((item) => item.name != 'Kosher' && item.name != 'Halal')}
            />
        ) : null;
    }, [Comp, filters, searchParams]);

    return (
        <FiltersStyle>
            {makeCategories}
            {makeSale}
            {makeBrands}
            {makePrice}
            {makeReligiousBased}
            {makeDietary}
            {(!searchParams.has('sort') && searchParams.size) || (searchParams.has('sort') && searchParams.size > 1) ? (
                <div className='selected-wrapper'>
                    {winWidth >= 1024 ? (
                        <Text
                            text={'clearAll'}
                            onClick={clearFilter}
                            className='underline h3 font-futura-medium black cursor-pointer is-desktop'
                        />
                    ) : null}
                    <div className='selected-inner-wrapper'>
                        {selectedFiltersTypes.map(selectedFiltersType => {
                            return selectedFilters({ ...selectedFiltersType }).map((item, index) => (
                                <SelectedItem
                                    index={index}
                                    item={item}
                                    onFilterChange={onFilterChange}
                                    filterKey={selectedFiltersType.filterKey}
                                    multiple={selectedFiltersType.multiple}
                                />
                            ))
                        })}
                    </div>

                    {!(winWidth >= 1024) ? (
                        <Text
                            text={'clearAll'}
                            onClick={clearFilter}
                            className='underline h3 font-futura-medium black cursor-pointer is-mobile'
                        />
                    ) : null}
                </div>
            ) : null}
        </FiltersStyle>
    );
};

export default withUIContext(Filters, ['winWidth']);
